import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { NavActions } from '../components/NavBar/NavActions';
import { SearchBar } from '../components/SearchBar';
import { Badge } from '../components/SubscriptionAppBadge';
import { TableFilter } from '../components/TableFilter';
import type { Filter } from '../components/TableFilter';
import { ExportButton } from '../components/ExportButton';
import { Toast } from '../components/Toast';
import type { ToastProps } from '../components/Toast';
import { Button } from '../components/SubscriptionAppButton';
import { IconButton } from '../components/SubscriptionAppIconButton';
import { Pagination } from '../components/SubscriptionAppPagination';
import { Input } from '../components/SubscriptionAppInput';
import { Edit, X, ChevronDown, AlertTriangle, Info, ArrowRight, Shield, ToggleLeft, ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react';
// Tier thresholds
const TIER_THRESHOLDS = [{
  name: 'Enthusiast',
  min: 0,
  max: 499
}, {
  name: 'Connoisseur',
  min: 500,
  max: 1999
}, {
  name: 'Sommelier',
  min: 2000,
  max: 4999
}, {
  name: 'Vintner',
  min: 5000,
  max: Infinity
}];
const getTierForPoints = (points: number): string => {
  for (const tier of TIER_THRESHOLDS) {
    if (points >= tier.min && points <= tier.max) return tier.name;
  }
  return 'Enthusiast';
};
const REASON_PRESETS = ['Event bonus', 'Program migration', 'Correction', 'Goodwill gesture', 'Promotional campaign', 'Other'];
const ADD_REASON_PRESETS = ['Cellar door visit', 'Wine club event', 'Tasting experience', 'Program migration', 'Correction', 'Goodwill gesture', 'Promotional campaign', 'Other'];
const REMOVE_REASON_PRESETS = ['Correction', 'Program migration', 'Points expiry', 'Refund adjustment', 'Order cancellation', 'Other'];
// Mock Data
const mockMembers = [{
  id: '1',
  name: 'Sarah Johnson',
  email: 'sarah.j@example.com',
  tier: 'Sommelier',
  points: 2450,
  lifetimePoints: 5800,
  lastActivity: '2 hours ago',
  status: 'Active'
}, {
  id: '2',
  name: 'James Chen',
  email: 'james.c@example.com',
  tier: 'Connoisseur',
  points: 850,
  lifetimePoints: 1200,
  lastActivity: '5 hours ago',
  status: 'Active'
}, {
  id: '3',
  name: 'Emma Wilson',
  email: 'emma.w@example.com',
  tier: 'Enthusiast',
  points: 120,
  lifetimePoints: 350,
  lastActivity: '1 day ago',
  status: 'Active'
}, {
  id: '4',
  name: 'David Park',
  email: 'david.p@example.com',
  tier: 'Vintner',
  points: 15600,
  lifetimePoints: 42000,
  lastActivity: '3 days ago',
  status: 'Active'
}, {
  id: '5',
  name: 'Lisa Wang',
  email: 'lisa.w@example.com',
  tier: 'Connoisseur',
  points: 600,
  lifetimePoints: 950,
  lastActivity: '1 week ago',
  status: 'Paused'
}, {
  id: '6',
  name: 'Tom Harris',
  email: 'tom.h@example.com',
  tier: 'Enthusiast',
  points: 50,
  lifetimePoints: 150,
  lastActivity: '2 weeks ago',
  status: 'Active'
}, {
  id: '7',
  name: 'Sophie Miller',
  email: 'sophie.m@example.com',
  tier: 'Sommelier',
  points: 3100,
  lifetimePoints: 6200,
  lastActivity: '1 month ago',
  status: 'Active'
}, {
  id: '8',
  name: 'Michael Brown',
  email: 'michael.b@example.com',
  tier: 'Enthusiast',
  points: 0,
  lifetimePoints: 50,
  lastActivity: '3 months ago',
  status: 'Expired'
}];
export function LoyaltyMembersPage() {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedMembers, setSelectedMembers] = useState<Set<string>>(new Set());
  const [filterValues, setFilterValues] = useState<Record<string, string[]>>({});
  const [addedFilters, setAddedFilters] = useState<Set<string>>(new Set());
  const [openFilterDropdowns, setOpenFilterDropdowns] = useState<Set<string>>(new Set());
  const [isBulkModalOpen, setIsBulkModalOpen] = useState(false);
  const [adjustmentType, setAdjustmentType] = useState<'add' | 'remove' | 'reset'>('add');
  const [adjustmentAmount, setAdjustmentAmount] = useState('');
  const [adjustmentReason, setAdjustmentReason] = useState('');
  const [adjustmentReasonPreset, setAdjustmentReasonPreset] = useState('');
  const [adjustmentReasonCustom, setAdjustmentReasonCustom] = useState('');
  const [showTierPreview, setShowTierPreview] = useState(false);
  const [isReasonDropdownOpen, setIsReasonDropdownOpen] = useState(false);
  const [resetConfirmation, setResetConfirmation] = useState('');
  const [toasts, setToasts] = useState<ToastProps[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  // Sort state
  const [sortConfig, setSortConfig] = useState<{
    key: string;
    direction: 'asc' | 'desc';
  } | null>(null);
  // Bulk Change Tier state
  const [isTierModalOpen, setIsTierModalOpen] = useState(false);
  const [selectedTargetTier, setSelectedTargetTier] = useState('');
  const [lockTier, setLockTier] = useState(false);
  const [tierReasonPreset, setTierReasonPreset] = useState('');
  const [tierReasonCustom, setTierReasonCustom] = useState('');
  const [isTierReasonDropdownOpen, setIsTierReasonDropdownOpen] = useState(false);
  const [isTierDropdownOpen, setIsTierDropdownOpen] = useState(false);
  // Change Loyalty Status state
  const [isStatusModalOpen, setIsStatusModalOpen] = useState(false);
  const [statusAction, setStatusAction] = useState<'pause' | 'resume'>('pause');
  const [statusReason, setStatusReason] = useState('');
  const [notifyMembers, setNotifyMembers] = useState(true);
  const removeToast = (id: string) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  };
  const handleOpenBulkModal = () => {
    setAdjustmentType('add');
    setAdjustmentAmount('');
    setAdjustmentReason('');
    setAdjustmentReasonPreset('');
    setAdjustmentReasonCustom('');
    setShowTierPreview(false);
    setIsReasonDropdownOpen(false);
    setResetConfirmation('');
    setIsBulkModalOpen(true);
  };
  const handleOpenTierModal = () => {
    setSelectedTargetTier('');
    setLockTier(false);
    setTierReasonPreset('');
    setTierReasonCustom('');
    setIsTierReasonDropdownOpen(false);
    setIsTierDropdownOpen(false);
    setIsTierModalOpen(true);
  };
  const handleOpenStatusModal = () => {
    setStatusAction('pause');
    setStatusReason('');
    setNotifyMembers(true);
    setIsStatusModalOpen(true);
  };
  const getTierRank = (tierName: string): number => {
    const index = TIER_THRESHOLDS.findIndex(t => t.name === tierName);
    return index >= 0 ? index : 0;
  };
  const availableFilters: Filter[] = [{
    id: 'tier',
    label: 'Tier',
    options: [{
      id: '1',
      label: 'Enthusiast',
      value: 'Enthusiast'
    }, {
      id: '2',
      label: 'Connoisseur',
      value: 'Connoisseur'
    }, {
      id: '3',
      label: 'Sommelier',
      value: 'Sommelier'
    }, {
      id: '4',
      label: 'Vintner',
      value: 'Vintner'
    }],
    selectedValues: filterValues.tier || [],
    multiSelect: true,
    isOpen: openFilterDropdowns.has('tier')
  }, {
    id: 'status',
    label: 'Status',
    options: [{
      id: '1',
      label: 'Active',
      value: 'Active'
    }, {
      id: '2',
      label: 'Paused',
      value: 'Paused'
    }, {
      id: '3',
      label: 'Expired',
      value: 'Expired'
    }],
    selectedValues: filterValues.status || [],
    multiSelect: true,
    isOpen: openFilterDropdowns.has('status')
  }, {
    id: 'points',
    label: 'Points',
    options: [{
      id: '1',
      label: '0 - 500',
      value: '0-500'
    }, {
      id: '2',
      label: '500 - 2000',
      value: '500-2000'
    }, {
      id: '3',
      label: '2000+',
      value: '2000+'
    }],
    selectedValues: filterValues.points || [],
    multiSelect: true,
    isOpen: openFilterDropdowns.has('points')
  }];
  const handleFilterChange = (filterId: string, value: string | string[]) => {
    setFilterValues(prev => ({
      ...prev,
      [filterId]: Array.isArray(value) ? value : [value]
    }));
  };
  const handleFilterRemove = (filterId: string) => {
    const newAddedFilters = new Set(addedFilters);
    newAddedFilters.delete(filterId);
    setAddedFilters(newAddedFilters);
    setFilterValues(prev => ({
      ...prev,
      [filterId]: []
    }));
  };
  const handleToggleFilterDropdown = (filterId: string) => {
    const newOpen = new Set(openFilterDropdowns);
    if (newOpen.has(filterId)) {
      newOpen.delete(filterId);
    } else {
      newOpen.add(filterId);
    }
    setOpenFilterDropdowns(newOpen);
  };
  const handleAddFilter = (filterId: string) => {
    const newAdded = new Set(addedFilters);
    newAdded.add(filterId);
    setAddedFilters(newAdded);
  };
  const handleClearAll = () => {
    setFilterValues({});
    setAddedFilters(new Set());
    setSearchQuery('');
  };
  // Sort handler
  const handleSort = (columnKey: string) => {
    setSortConfig(current => {
      if (!current || current.key !== columnKey) {
        return {
          key: columnKey,
          direction: 'asc'
        };
      }
      if (current.direction === 'asc') {
        return {
          key: columnKey,
          direction: 'desc'
        };
      }
      return null;
    });
  };
  // Filtered and sorted members
  const filteredAndSortedMembers = useMemo(() => {
    let filtered = mockMembers.filter(member => {
      // Search
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        if (!member.name.toLowerCase().includes(query) && !member.email.toLowerCase().includes(query)) {
          return false;
        }
      }
      // Tier filter
      if (filterValues.tier && filterValues.tier.length > 0) {
        if (!filterValues.tier.includes(member.tier)) return false;
      }
      // Status filter
      if (filterValues.status && filterValues.status.length > 0) {
        if (!filterValues.status.includes(member.status)) return false;
      }
      // Points filter
      if (filterValues.points && filterValues.points.length > 0) {
        const pointsMatch = filterValues.points.some(value => {
          if (value === '0-500') return member.points >= 0 && member.points <= 500;
          if (value === '500-2000') return member.points > 500 && member.points <= 2000;
          if (value === '2000+') return member.points > 2000;
          return false;
        });
        if (!pointsMatch) return false;
      }
      return true;
    });
    // Apply sorting
    if (sortConfig) {
      filtered = [...filtered].sort((a, b) => {
        let aValue: any;
        let bValue: any;
        switch (sortConfig.key) {
          case 'member':
            aValue = a.name.toLowerCase();
            bValue = b.name.toLowerCase();
            break;
          case 'tier':
            const tierOrder: Record<string, number> = {
              Enthusiast: 0,
              Connoisseur: 1,
              Sommelier: 2,
              Vintner: 3
            };
            aValue = tierOrder[a.tier] ?? 0;
            bValue = tierOrder[b.tier] ?? 0;
            break;
          case 'points':
            aValue = a.points;
            bValue = b.points;
            break;
          case 'lifetimePoints':
            aValue = a.lifetimePoints;
            bValue = b.lifetimePoints;
            break;
          case 'lastActivity':
            // Parse relative time to sortable value (lower = more recent)
            const parseActivity = (str: string): number => {
              if (str.includes('hour')) return parseInt(str) || 1;
              if (str.includes('day')) return (parseInt(str) || 1) * 24;
              if (str.includes('week')) return (parseInt(str) || 1) * 168;
              if (str.includes('month')) return (parseInt(str) || 1) * 720;
              return 9999;
            };
            aValue = parseActivity(a.lastActivity);
            bValue = parseActivity(b.lastActivity);
            break;
          case 'status':
            aValue = a.status.toLowerCase();
            bValue = b.status.toLowerCase();
            break;
          default:
            return 0;
        }
        if (typeof aValue === 'number' && typeof bValue === 'number') {
          return sortConfig.direction === 'asc' ? aValue - bValue : bValue - aValue;
        }
        if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
        if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
    }
    return filtered;
  }, [mockMembers, searchQuery, filterValues, sortConfig]);
  const toggleSelectAll = () => {
    if (selectedMembers.size === filteredAndSortedMembers.length) {
      setSelectedMembers(new Set());
    } else {
      setSelectedMembers(new Set(filteredAndSortedMembers.map(m => m.id)));
    }
  };
  const toggleSelectMember = (id: string) => {
    const newSelected = new Set(selectedMembers);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedMembers(newSelected);
  };
  const getTierBadgeVariant = (tier: string) => {
    switch (tier) {
      case 'Enthusiast':
        return 'success';
      // Green
      case 'Connoisseur':
        return 'info';
      // Blue
      case 'Sommelier':
        return 'default';
      // Purple/Default (using default as purple isn't explicit in Badge props, checking Badge.tsx... default is gray/slate. Wait, request said "purple for Sommelier". Badge.tsx has success, warning, error, info, default. I'll use 'default' for now or maybe 'info' again if needed, but let's stick to available variants. Actually, I can use custom classes if needed, but Badge component encapsulates styles. Let's use 'info' for Connoisseur (Blue), 'success' for Enthusiast (Green). For Sommelier (Purple) and Vintner (Gold), I might need to use 'warning' for Gold. Purple isn't standard. I'll use 'default' for Sommelier and 'warning' for Vintner.)
      case 'Vintner':
        return 'warning';
      // Gold/Amber
      default:
        return 'default';
    }
  };
  const getStatusBadgeVariant = (status: string) => {
    switch (status) {
      case 'Active':
        return 'success';
      case 'Paused':
        return 'warning';
      case 'Expired':
        return 'error';
      default:
        return 'default';
    }
  };
  return <div className="bg-[#F1F5F9] min-h-full p-6" data-id="element-6426">
      {/* Header */}
      <div className="mb-6" data-id="element-6427">
        <div className="w-full h-16 bg-white border-b border-[#CBD5E1] px-6 flex items-center gap-4" data-id="element-6428">
          <h1 className="text-lg font-semibold text-[#0F172A] m-0" data-id="element-6429">
            Loyalty Members
          </h1>
          <div className="flex-1" data-id="element-6430" />
          <NavActions data-id="element-6431" />
        </div>
      </div>

      {/* Filter Bar */}
      <div className="bg-white border border-ocean-border-subtle rounded-lg p-4 mb-6 shadow-sm" data-id="element-6432">
        <div className="flex flex-col gap-4" data-id="element-6433">
          <div className="flex items-center gap-3" data-id="element-6434">
            <div className="flex-1 max-w-md" data-id="element-6435">
              <SearchBar placeholder="Search by name or email..." value={searchQuery} onChange={setSearchQuery} data-id="element-6436" />
            </div>
            <TableFilter filters={availableFilters} onFilterChange={handleFilterChange} onFilterRemove={handleFilterRemove} onToggleDropdown={handleToggleFilterDropdown} addedFilterIds={addedFilters} onAddFilter={handleAddFilter} renderOnlyButton={true} data-id="element-6437" />
            <ExportButton variant="contacts" data-id="element-6438" />
          </div>
          {addedFilters.size > 0 && <div className="flex items-center gap-3" data-id="element-6439">
              <TableFilter filters={availableFilters} onFilterChange={handleFilterChange} onFilterRemove={handleFilterRemove} onToggleDropdown={handleToggleFilterDropdown} addedFilterIds={addedFilters} onAddFilter={handleAddFilter} renderOnlyChips={true} data-id="element-6440" />
              <Button variant="ghost" size="sm" onClick={handleClearAll} data-id="element-6441">
                Clear all
              </Button>
            </div>}
        </div>
      </div>

      {/* Data Table */}
      <div className="bg-white border border-ocean-border-subtle rounded-lg shadow-sm overflow-hidden" data-id="element-6442">
        {selectedMembers.size > 0 && <div className="bg-white border-b border-[#E2E8F0] px-6 py-4" data-id="element-6443">
            <div className="flex items-center gap-4" data-id="element-6444">
              <span className="text-[14px] font-semibold text-[#0F172A]" data-id="element-6445">
                {selectedMembers.size} selected
              </span>
              <button className="text-[13px] text-[#64748B] hover:text-[#0F172A] transition-colors cursor-pointer" onClick={() => setSelectedMembers(new Set())} data-id="element-6446">
                Clear selection
              </button>
              <div className="flex-1" data-id="element-6447" />
              <Button variant="outline" size="md" onClick={handleOpenBulkModal} iconLeft={<Edit className="w-4 h-4" data-id="element-6449" />} data-id="element-6448">
                Bulk Adjust
              </Button>
              <Button variant="outline" size="md" onClick={handleOpenTierModal} iconLeft={<Shield className="w-4 h-4" data-id="element-6451" />} data-id="element-6450">
                Change Tier
              </Button>
              <Button variant="outline" size="md" onClick={handleOpenStatusModal} iconLeft={<ToggleLeft className="w-4 h-4" data-id="element-6453" />} data-id="element-6452">
                Change Status
              </Button>
            </div>
          </div>}
        <div className="overflow-x-auto" data-id="element-6454">
          <table className="w-full" data-id="element-6455">
            <thead className="bg-[#F1F5F9]" data-id="element-6456">
              <tr data-id="element-6457">
                <th className="w-12 px-4 py-3 border-b border-[#E2E8F0]" data-id="element-6458">
                  <div className="flex items-center justify-center" data-id="element-6459">
                    <input type="checkbox" className="w-[18px] h-[18px] rounded border-[#CBD5E1] text-[#0EA5E9] focus:ring-[#0EA5E9] cursor-pointer" checked={selectedMembers.size === filteredAndSortedMembers.length && filteredAndSortedMembers.length > 0} onChange={toggleSelectAll} data-id="element-6460" />
                  </div>
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-[#475569] uppercase tracking-wider" data-id="element-6461">
                  <button onClick={() => handleSort('member')} className="flex items-center gap-2 hover:text-[#0EA5E9] transition-colors" data-id="element-6462">
                    Member
                    {sortConfig?.key === 'member' ? sortConfig.direction === 'asc' ? <ArrowUp className="w-3.5 h-3.5 text-[#0EA5E9]" data-id="element-6463" /> : <ArrowDown className="w-3.5 h-3.5 text-[#0EA5E9]" data-id="element-6464" /> : <ArrowUpDown className="w-3.5 h-3.5 text-[#CBD5E1]" data-id="element-6465" />}
                  </button>
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-[#475569] uppercase tracking-wider" data-id="element-6466">
                  <button onClick={() => handleSort('tier')} className="flex items-center gap-2 hover:text-[#0EA5E9] transition-colors" data-id="element-6467">
                    Tier
                    {sortConfig?.key === 'tier' ? sortConfig.direction === 'asc' ? <ArrowUp className="w-3.5 h-3.5 text-[#0EA5E9]" data-id="element-6468" /> : <ArrowDown className="w-3.5 h-3.5 text-[#0EA5E9]" data-id="element-6469" /> : <ArrowUpDown className="w-3.5 h-3.5 text-[#CBD5E1]" data-id="element-6470" />}
                  </button>
                </th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-[#475569] uppercase tracking-wider" data-id="element-6471">
                  <button onClick={() => handleSort('points')} className="flex items-center gap-2 hover:text-[#0EA5E9] transition-colors justify-end" data-id="element-6472">
                    Points
                    {sortConfig?.key === 'points' ? sortConfig.direction === 'asc' ? <ArrowUp className="w-3.5 h-3.5 text-[#0EA5E9]" data-id="element-6473" /> : <ArrowDown className="w-3.5 h-3.5 text-[#0EA5E9]" data-id="element-6474" /> : <ArrowUpDown className="w-3.5 h-3.5 text-[#CBD5E1]" data-id="element-6475" />}
                  </button>
                </th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-[#475569] uppercase tracking-wider" data-id="element-6476">
                  <button onClick={() => handleSort('lifetimePoints')} className="flex items-center gap-2 hover:text-[#0EA5E9] transition-colors justify-end" data-id="element-6477">
                    Lifetime Points
                    {sortConfig?.key === 'lifetimePoints' ? sortConfig.direction === 'asc' ? <ArrowUp className="w-3.5 h-3.5 text-[#0EA5E9]" data-id="element-6478" /> : <ArrowDown className="w-3.5 h-3.5 text-[#0EA5E9]" data-id="element-6479" /> : <ArrowUpDown className="w-3.5 h-3.5 text-[#CBD5E1]" data-id="element-6480" />}
                  </button>
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-[#475569] uppercase tracking-wider" data-id="element-6481">
                  <button onClick={() => handleSort('lastActivity')} className="flex items-center gap-2 hover:text-[#0EA5E9] transition-colors" data-id="element-6482">
                    Last Activity
                    {sortConfig?.key === 'lastActivity' ? sortConfig.direction === 'asc' ? <ArrowUp className="w-3.5 h-3.5 text-[#0EA5E9]" data-id="element-6483" /> : <ArrowDown className="w-3.5 h-3.5 text-[#0EA5E9]" data-id="element-6484" /> : <ArrowUpDown className="w-3.5 h-3.5 text-[#CBD5E1]" data-id="element-6485" />}
                  </button>
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-[#475569] uppercase tracking-wider" data-id="element-6486">
                  <button onClick={() => handleSort('status')} className="flex items-center gap-2 hover:text-[#0EA5E9] transition-colors" data-id="element-6487">
                    Status
                    {sortConfig?.key === 'status' ? sortConfig.direction === 'asc' ? <ArrowUp className="w-3.5 h-3.5 text-[#0EA5E9]" data-id="element-6488" /> : <ArrowDown className="w-3.5 h-3.5 text-[#0EA5E9]" data-id="element-6489" /> : <ArrowUpDown className="w-3.5 h-3.5 text-[#CBD5E1]" data-id="element-6490" />}
                  </button>
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#E2E8F0]" data-id="element-6491">
              {filteredAndSortedMembers.map(member => {
              const isSelected = selectedMembers.has(member.id);
              return <tr key={member.id} className={`transition-colors cursor-pointer ${isSelected ? 'bg-[#F0F9FF]' : 'hover:bg-[#F8FAFC]'}`} onClick={() => navigate('/contacts/1', {
                state: {
                  activeTab: 'Loyalty'
                }
              })} data-id="element-6492">
                    <td className="px-4 py-4" onClick={e => e.stopPropagation()} data-id="element-6493">
                      <div className="flex items-center justify-center" data-id="element-6494">
                        <input type="checkbox" className="w-[18px] h-[18px] rounded border-[#CBD5E1] text-[#0EA5E9] focus:ring-[#0EA5E9] cursor-pointer" checked={isSelected} onChange={() => toggleSelectMember(member.id)} data-id="element-6495" />
                      </div>
                    </td>
                    <td className="px-4 py-4" data-id="element-6496">
                      <div data-id="element-6497">
                        <div className="text-sm font-medium text-[#0F172A] hover:text-[#0EA5E9] transition-colors" data-id="element-6498">
                          {member.name}
                        </div>
                        <div className="text-[13px] text-[#94A3B8] mt-1" data-id="element-6499">
                          {member.email}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-4" data-id="element-6500">
                      <Badge variant={getTierBadgeVariant(member.tier)} data-id="element-6501">
                        {member.tier}
                      </Badge>
                    </td>
                    <td className="px-4 py-4 text-right text-sm font-medium text-[#0F172A]" data-id="element-6502">
                      {member.points.toLocaleString()}
                    </td>
                    <td className="px-4 py-4 text-right text-sm text-[#94A3B8]" data-id="element-6503">
                      {member.lifetimePoints.toLocaleString()}
                    </td>
                    <td className="px-4 py-4 text-sm text-[#475569]" data-id="element-6504">
                      {member.lastActivity}
                    </td>
                    <td className="px-4 py-4" data-id="element-6505">
                      <Badge variant={getStatusBadgeVariant(member.status)} data-id="element-6506">
                        {member.status}
                      </Badge>
                    </td>
                  </tr>;
            })}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="px-6 py-4 border-t border-ocean-border-subtle bg-white" data-id="element-6507">
          <Pagination currentPage={currentPage} totalPages={50} onPageChange={setCurrentPage} itemsPerPage={25} totalItems={1247} data-id="element-6508" />
        </div>
      </div>

      {/* Bulk Adjust Points Modal */}
      {isBulkModalOpen && <>
          <div className="fixed inset-0 bg-black/50 z-50" onClick={() => setIsBulkModalOpen(false)} data-id="element-6509" />
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-id="element-6510">
            <div className="bg-white rounded-lg shadow-xl w-[420px] max-h-[90vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()} data-id="element-6511">
              {/* Modal Header */}
              <div className="px-6 py-4 border-b border-[#E2E8F0] flex items-center justify-between" data-id="element-6512">
                <h2 className="text-lg font-semibold text-[#0F172A]" data-id="element-6513">
                  Bulk Adjust Points
                </h2>
                <IconButton icon={<X className="w-5 h-5" data-id="element-6515" />} label="Close modal" variant="ghost" onClick={() => setIsBulkModalOpen(false)} data-id="element-6514" />
              </div>

              {/* Modal Content */}
              <div className="px-6 py-6 space-y-5 overflow-y-auto" data-id="element-6516">
                <p className="text-sm text-[#475569]" data-id="element-6517">
                  Adjust points for{' '}
                  <span className="font-semibold text-[#0F172A]" data-id="element-6518">
                    {selectedMembers.size > 0 ? selectedMembers.size : mockMembers.length}
                  </span>{' '}
                  {selectedMembers.size > 0 ? 'selected' : 'all'} members
                </p>

                {/* Adjustment Type */}
                <div data-id="element-6519">
                  <label className="block text-xs font-medium text-[#475569] mb-2" data-id="element-6520">
                    Adjustment Type
                  </label>
                  <div className="flex gap-4 flex-wrap" data-id="element-6521">
                    <label className="flex items-center gap-2 cursor-pointer" data-id="element-6522">
                      <div className={`w-4 h-4 rounded-full border flex items-center justify-center ${adjustmentType === 'add' ? 'border-[#0EA5E9]' : 'border-[#CBD5E1]'}`} onClick={() => {
                    setAdjustmentType('add');
                    setAdjustmentReasonPreset('');
                    setAdjustmentReasonCustom('');
                    setIsReasonDropdownOpen(false);
                    setResetConfirmation('');
                  }} data-id="element-6523">
                        {adjustmentType === 'add' && <div className="w-2 h-2 rounded-full bg-[#0EA5E9]" data-id="element-6524" />}
                      </div>
                      <span className="text-sm text-[#0F172A]" data-id="element-6525">Add Points</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer" data-id="element-6526">
                      <div className={`w-4 h-4 rounded-full border flex items-center justify-center ${adjustmentType === 'remove' ? 'border-[#0EA5E9]' : 'border-[#CBD5E1]'}`} onClick={() => {
                    setAdjustmentType('remove');
                    setAdjustmentReasonPreset('');
                    setAdjustmentReasonCustom('');
                    setIsReasonDropdownOpen(false);
                    setResetConfirmation('');
                  }} data-id="element-6527">
                        {adjustmentType === 'remove' && <div className="w-2 h-2 rounded-full bg-[#0EA5E9]" data-id="element-6528" />}
                      </div>
                      <span className="text-sm text-[#0F172A]" data-id="element-6529">
                        Remove Points
                      </span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer" data-id="element-6530">
                      <div className={`w-4 h-4 rounded-full border flex items-center justify-center ${adjustmentType === 'reset' ? 'border-[#EF4444]' : 'border-[#CBD5E1]'}`} onClick={() => {
                    setAdjustmentType('reset');
                    setAdjustmentReasonPreset('');
                    setAdjustmentReasonCustom('');
                    setIsReasonDropdownOpen(false);
                    setResetConfirmation('');
                  }} data-id="element-6531">
                        {adjustmentType === 'reset' && <div className="w-2 h-2 rounded-full bg-[#EF4444]" data-id="element-6532" />}
                      </div>
                      <span className="text-sm text-[#0F172A]" data-id="element-6533">
                        Reset to Zero
                      </span>
                    </label>
                  </div>
                </div>

                {/* Reset to Zero Warning & Confirmation */}
                {adjustmentType === 'reset' && (() => {
              const targetMembers = selectedMembers.size > 0 ? mockMembers.filter(m => selectedMembers.has(m.id)) : mockMembers;
              const memberCount = targetMembers.length;
              return <div className="space-y-4" data-id="element-6534">
                        {/* Destructive warning */}
                        <div className="bg-[#FEE2E2] border border-[#FECACA] rounded-[6px] px-4 py-3 flex items-start gap-2.5" data-id="element-6535">
                          <AlertTriangle className="w-4 h-4 text-[#EF4444] mt-0.5 shrink-0" data-id="element-6536" />
                          <p className="text-[13px] text-[#991B1B]" data-id="element-6537">
                            This will set the current points balance to 0 for{' '}
                            <span className="font-semibold" data-id="element-6538">{memberCount}</span>{' '}
                            member{memberCount !== 1 ? 's' : ''}. Lifetime
                            points will not be affected. This action cannot be
                            undone.
                          </p>
                        </div>

                        {/* Confirmation input */}
                        <div data-id="element-6539">
                          <label className="block text-[12px] font-medium text-[#475569] mb-1.5" data-id="element-6540">
                            Confirm Reset
                          </label>
                          <input type="text" value={resetConfirmation} onChange={e => setResetConfirmation(e.target.value)} placeholder="Type RESET to confirm" className="w-full h-[40px] px-3 bg-white border border-[#CBD5E1] rounded-[6px] text-[14px] text-[#0F172A] placeholder:text-[#94A3B8] focus:outline-none focus:border-[#0EA5E9] focus:ring-[3px] focus:ring-[#0EA5E9]/20 transition-all duration-200" data-id="element-6541" />
                          <p className="text-[12px] text-[#64748B] mt-1" data-id="element-6542">
                            Type RESET to confirm
                          </p>
                        </div>
                      </div>;
            })()}

                {/* Amount - hidden for reset */}
                {adjustmentType !== 'reset' && <Input type="number" label="Amount" value={adjustmentAmount} onChange={e => setAdjustmentAmount(e.target.value)} placeholder="0" data-id="element-6543" />}

                {/* Reason Dropdown */}
                <div data-id="element-6544">
                  <label className="block text-[12px] font-medium text-[#475569] mb-1.5" data-id="element-6545">
                    Reason <span className="text-[#EF4444]" data-id="element-6546">*</span>
                  </label>
                  <div className="relative" data-id="element-6547">
                    <button type="button" onClick={() => setIsReasonDropdownOpen(!isReasonDropdownOpen)} className="w-full h-[40px] px-3 bg-white border border-[#CBD5E1] rounded-[6px] text-[14px] text-left flex items-center justify-between focus:outline-none focus:border-[#0EA5E9] focus:ring-[3px] focus:ring-[#0EA5E9]/20 transition-all duration-200" data-id="element-6548">
                      <span className={adjustmentReasonPreset ? 'text-[#0F172A]' : 'text-[#94A3B8]'} data-id="element-6549">
                        {adjustmentReasonPreset || 'Select a reason...'}
                      </span>
                      <ChevronDown className={`w-4 h-4 text-[#475569] transition-transform ${isReasonDropdownOpen ? 'rotate-180' : ''}`} data-id="element-6550" />
                    </button>
                    {isReasonDropdownOpen && <div className="absolute z-10 w-full mt-1 bg-white border border-[#E2E8F0] rounded-[6px] shadow-lg overflow-hidden" data-id="element-6551">
                        {(adjustmentType === 'add' ? ADD_REASON_PRESETS : REMOVE_REASON_PRESETS).map(reason => <button key={reason} type="button" onClick={() => {
                    setAdjustmentReasonPreset(reason);
                    setIsReasonDropdownOpen(false);
                    if (reason !== 'Other') {
                      setAdjustmentReasonCustom('');
                    }
                  }} className={`w-full px-3 py-2.5 text-[14px] text-left hover:bg-[#F1F5F9] transition-colors ${adjustmentReasonPreset === reason ? 'bg-[#F0F9FF] text-[#0EA5E9] font-medium' : 'text-[#0F172A]'}`} data-id="element-6552">
                            {reason}
                          </button>)}
                      </div>}
                  </div>
                  {adjustmentReasonPreset === 'Other' && <input type="text" value={adjustmentReasonCustom} onChange={e => setAdjustmentReasonCustom(e.target.value)} placeholder="Enter custom reason..." className="w-full h-[40px] px-3 mt-2 bg-white border border-[#CBD5E1] rounded-[6px] text-[14px] text-[#0F172A] placeholder:text-[#94A3B8] focus:outline-none focus:border-[#0EA5E9] focus:ring-[3px] focus:ring-[#0EA5E9]/20 transition-all duration-200" data-id="element-6553" />}
                </div>

                {/* Preview Summary & Warnings (add/remove only) */}
                {adjustmentAmount && parseInt(adjustmentAmount) > 0 && (() => {
              const amount = parseInt(adjustmentAmount);
              const targetMembers = selectedMembers.size > 0 ? mockMembers.filter(m => selectedMembers.has(m.id)) : mockMembers;
              const memberCount = targetMembers.length;
              // Calculate members going below zero
              const membersGoingBelowZero = adjustmentType === 'remove' ? targetMembers.filter(m => m.points < amount) : [];
              // Calculate tier changes (exclude members going below zero since they already have a warning)
              const membersGoingBelowZeroIds = new Set(membersGoingBelowZero.map(m => m.id));
              const tierChanges = targetMembers.filter(m => !membersGoingBelowZeroIds.has(m.id)).map(m => {
                const newPoints = adjustmentType === 'add' ? m.points + amount : Math.max(0, m.points - amount);
                const currentTier = getTierForPoints(m.points);
                const newTier = getTierForPoints(newPoints);
                if (currentTier !== newTier) {
                  return {
                    name: m.name,
                    currentTier,
                    newTier
                  };
                }
                return null;
              }).filter(Boolean) as {
                name: string;
                currentTier: string;
                newTier: string;
              }[];
              return <div className="space-y-3" data-id="element-6554">
                        {/* Summary */}
                        <div className="bg-[#F1F5F9] rounded-[6px] px-4 py-3" data-id="element-6555">
                          <p className="text-[13px] font-medium text-[#0F172A]" data-id="element-6556">
                            This will{' '}
                            {adjustmentType === 'add' ? 'add' : 'remove'}{' '}
                            <span className="font-semibold" data-id="element-6557">
                              {amount.toLocaleString()}
                            </span>{' '}
                            points
                            {adjustmentType === 'add' ? ' to ' : ' from '}
                            <span className="font-semibold" data-id="element-6558">
                              {memberCount}
                            </span>{' '}
                            member{memberCount !== 1 ? 's' : ''}
                          </p>
                        </div>

                        {/* Below zero warning */}
                        {membersGoingBelowZero.length > 0 && <div className="bg-[#FEF3C7] border border-[#FDE68A] rounded-[6px] px-4 py-3 flex items-start gap-2.5" data-id="element-6559">
                            <AlertTriangle className="w-4 h-4 text-[#D97706] mt-0.5 shrink-0" data-id="element-6560" />
                            <p className="text-[13px] text-[#92400E]" data-id="element-6561">
                              <span className="font-semibold" data-id="element-6562">
                                {membersGoingBelowZero.length}
                              </span>{' '}
                              member
                              {membersGoingBelowZero.length !== 1 ? 's' : ''}{' '}
                              will be set to 0 points (insufficient balance)
                            </p>
                          </div>}

                        {/* Tier change notice */}
                        {tierChanges.length > 0 && <div className="bg-[#DBEAFE] border border-[#BFDBFE] rounded-[6px] px-4 py-3" data-id="element-6563">
                            <div className="flex items-start gap-2.5" data-id="element-6564">
                              <Info className="w-4 h-4 text-[#2563EB] mt-0.5 shrink-0" data-id="element-6565" />
                              <div className="flex-1" data-id="element-6566">
                                <p className="text-[13px] text-[#1E40AF]" data-id="element-6567">
                                  <span className="font-semibold" data-id="element-6568">
                                    {tierChanges.length}
                                  </span>{' '}
                                  member{tierChanges.length !== 1 ? 's' : ''}{' '}
                                  will move to a new tier as a result
                                </p>
                                <button type="button" onClick={() => setShowTierPreview(!showTierPreview)} className="text-[12px] font-medium text-[#2563EB] hover:text-[#1D4ED8] mt-1 transition-colors" data-id="element-6569">
                                  {showTierPreview ? 'Hide changes' : 'Preview changes'}
                                </button>
                              </div>
                            </div>
                            {showTierPreview && <div className="mt-3 pt-3 border-t border-[#BFDBFE] space-y-2" data-id="element-6570">
                                {tierChanges.map((change, i) => <div key={i} className="flex items-center gap-2 text-[12px]" data-id="element-6571">
                                    <span className="text-[#1E40AF] font-medium truncate max-w-[120px]" data-id="element-6572">
                                      {change.name}
                                    </span>
                                    <span className="text-[#60A5FA]" data-id="element-6573">
                                      {change.currentTier}
                                    </span>
                                    <ArrowRight className="w-3 h-3 text-[#60A5FA] shrink-0" data-id="element-6574" />
                                    <span className="text-[#1E40AF] font-semibold" data-id="element-6575">
                                      {change.newTier}
                                    </span>
                                  </div>)}
                              </div>}
                          </div>}
                      </div>;
            })()}
              </div>

              {/* Modal Footer */}
              <div className="px-6 py-4 border-t border-[#E2E8F0] flex items-center justify-end gap-3" data-id="element-6576">
                <Button variant="ghost" size="md" onClick={() => setIsBulkModalOpen(false)} data-id="element-6577">
                  Cancel
                </Button>
                {adjustmentType === 'reset' ? <Button variant="danger" size="md" disabled={resetConfirmation !== 'RESET'} onClick={() => {
              setIsBulkModalOpen(false);
            }} data-id="element-6578">
                    Reset Points
                  </Button> : <Button variant="primary" size="md" onClick={() => {
              setIsBulkModalOpen(false);
            }} data-id="element-6579">
                    Apply Adjustment
                  </Button>}
              </div>
            </div>
          </div>
        </>}

      {/* Bulk Change Tier Modal */}
      {isTierModalOpen && <>
          <div className="fixed inset-0 bg-black/50 z-50" onClick={() => setIsTierModalOpen(false)} data-id="element-6580" />
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-id="element-6581">
            <div className="bg-white rounded-lg shadow-xl w-[420px] max-h-[90vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()} data-id="element-6582">
              {/* Modal Header */}
              <div className="px-6 py-4 border-b border-[#E2E8F0] flex items-center justify-between" data-id="element-6583">
                <h2 className="text-lg font-semibold text-[#0F172A]" data-id="element-6584">
                  Bulk Change Tier
                </h2>
                <IconButton icon={<X className="w-5 h-5" data-id="element-6586" />} label="Close modal" variant="ghost" onClick={() => setIsTierModalOpen(false)} data-id="element-6585" />
              </div>

              {/* Modal Content */}
              <div className="px-6 py-6 space-y-5 overflow-y-auto" data-id="element-6587">
                <p className="text-sm text-[#475569]" data-id="element-6588">
                  Change tier for{' '}
                  <span className="font-semibold text-[#0F172A]" data-id="element-6589">
                    {selectedMembers.size}
                  </span>{' '}
                  selected members
                </p>

                {/* Target Tier Dropdown */}
                <div data-id="element-6590">
                  <label className="block text-[12px] font-medium text-[#475569] mb-1.5" data-id="element-6591">
                    Target Tier <span className="text-[#EF4444]" data-id="element-6592">*</span>
                  </label>
                  <div className="relative" data-id="element-6593">
                    <button type="button" onClick={() => setIsTierDropdownOpen(!isTierDropdownOpen)} className="w-full h-[40px] px-3 bg-white border border-[#CBD5E1] rounded-[6px] text-[14px] text-left flex items-center justify-between focus:outline-none focus:border-[#0EA5E9] focus:ring-[3px] focus:ring-[#0EA5E9]/20 transition-all duration-200" data-id="element-6594">
                      <span className={selectedTargetTier ? 'text-[#0F172A]' : 'text-[#94A3B8]'} data-id="element-6595">
                        {selectedTargetTier || 'Select a tier...'}
                      </span>
                      <ChevronDown className={`w-4 h-4 text-[#475569] transition-transform ${isTierDropdownOpen ? 'rotate-180' : ''}`} data-id="element-6596" />
                    </button>
                    {isTierDropdownOpen && <div className="absolute z-10 w-full mt-1 bg-white border border-[#E2E8F0] rounded-[6px] shadow-lg overflow-hidden" data-id="element-6597">
                        {TIER_THRESHOLDS.map(tier => <button key={tier.name} type="button" onClick={() => {
                    setSelectedTargetTier(tier.name);
                    setIsTierDropdownOpen(false);
                  }} className={`w-full px-3 py-2.5 text-[14px] text-left hover:bg-[#F1F5F9] transition-colors ${selectedTargetTier === tier.name ? 'bg-[#F0F9FF] text-[#0EA5E9] font-medium' : 'text-[#0F172A]'}`} data-id="element-6598">
                            {tier.name}
                          </button>)}
                      </div>}
                  </div>
                </div>

                {/* Lock Tier Checkbox */}
                <div data-id="element-6599">
                  <label className="flex items-start gap-3 cursor-pointer" data-id="element-6600">
                    <div onClick={() => setLockTier(!lockTier)} className={`w-[18px] h-[18px] mt-0.5 rounded border flex items-center justify-center shrink-0 transition-colors ${lockTier ? 'bg-[#0EA5E9] border-[#0EA5E9]' : 'border-[#CBD5E1] bg-white'}`} data-id="element-6601">
                      {lockTier && <svg width="12" height="12" viewBox="0 0 12 12" fill="none" data-id="element-6602">
                          <path d="M2.5 6L5 8.5L9.5 3.5" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-6603" />
                        </svg>}
                    </div>
                    <div data-id="element-6604">
                      <span className="text-[14px] font-medium text-[#0F172A]" data-id="element-6605">
                        Lock tier — prevent automatic tier recalculation
                      </span>
                      <p className="text-[12px] text-[#64748B] mt-1" data-id="element-6606">
                        Locked members will stay in this tier regardless of
                        points balance until manually unlocked
                      </p>
                    </div>
                  </label>
                </div>

                {/* Reason Dropdown */}
                <div data-id="element-6607">
                  <label className="block text-[12px] font-medium text-[#475569] mb-1.5" data-id="element-6608">
                    Reason <span className="text-[#EF4444]" data-id="element-6609">*</span>
                  </label>
                  <div className="relative" data-id="element-6610">
                    <button type="button" onClick={() => setIsTierReasonDropdownOpen(!isTierReasonDropdownOpen)} className="w-full h-[40px] px-3 bg-white border border-[#CBD5E1] rounded-[6px] text-[14px] text-left flex items-center justify-between focus:outline-none focus:border-[#0EA5E9] focus:ring-[3px] focus:ring-[#0EA5E9]/20 transition-all duration-200" data-id="element-6611">
                      <span className={tierReasonPreset ? 'text-[#0F172A]' : 'text-[#94A3B8]'} data-id="element-6612">
                        {tierReasonPreset || 'Select a reason...'}
                      </span>
                      <ChevronDown className={`w-4 h-4 text-[#475569] transition-transform ${isTierReasonDropdownOpen ? 'rotate-180' : ''}`} data-id="element-6613" />
                    </button>
                    {isTierReasonDropdownOpen && <div className="absolute z-10 w-full mt-1 bg-white border border-[#E2E8F0] rounded-[6px] shadow-lg overflow-hidden" data-id="element-6614">
                        {REASON_PRESETS.map(reason => <button key={reason} type="button" onClick={() => {
                    setTierReasonPreset(reason);
                    setIsTierReasonDropdownOpen(false);
                    if (reason !== 'Other') {
                      setTierReasonCustom('');
                    }
                  }} className={`w-full px-3 py-2.5 text-[14px] text-left hover:bg-[#F1F5F9] transition-colors ${tierReasonPreset === reason ? 'bg-[#F0F9FF] text-[#0EA5E9] font-medium' : 'text-[#0F172A]'}`} data-id="element-6615">
                            {reason}
                          </button>)}
                      </div>}
                  </div>
                  {tierReasonPreset === 'Other' && <input type="text" value={tierReasonCustom} onChange={e => setTierReasonCustom(e.target.value)} placeholder="Enter custom reason..." className="w-full h-[40px] px-3 mt-2 bg-white border border-[#CBD5E1] rounded-[6px] text-[14px] text-[#0F172A] placeholder:text-[#94A3B8] focus:outline-none focus:border-[#0EA5E9] focus:ring-[3px] focus:ring-[#0EA5E9]/20 transition-all duration-200" data-id="element-6616" />}
                </div>

                {/* Preview Summary & Warnings */}
                {selectedTargetTier && (() => {
              const targetMembers = mockMembers.filter(m => selectedMembers.has(m.id));
              const memberCount = targetMembers.length;
              const targetRank = getTierRank(selectedTargetTier);
              const downgradedMembers = targetMembers.filter(m => getTierRank(m.tier) > targetRank);
              return <div className="space-y-3" data-id="element-6617">
                        {/* Summary */}
                        <div className="bg-[#F1F5F9] rounded-[6px] px-4 py-3" data-id="element-6618">
                          <p className="text-[13px] font-medium text-[#0F172A]" data-id="element-6619">
                            <span className="font-semibold" data-id="element-6620">{memberCount}</span>{' '}
                            member{memberCount !== 1 ? 's' : ''} will be moved
                            from various tiers to{' '}
                            <span className="font-semibold" data-id="element-6621">
                              {selectedTargetTier}
                            </span>
                          </p>
                        </div>

                        {/* Downgrade warning */}
                        {downgradedMembers.length > 0 && <div className="bg-[#FEF3C7] border border-[#FDE68A] rounded-[6px] px-4 py-3 flex items-start gap-2.5" data-id="element-6622">
                            <AlertTriangle className="w-4 h-4 text-[#D97706] mt-0.5 shrink-0" data-id="element-6623" />
                            <p className="text-[13px] text-[#92400E]" data-id="element-6624">
                              <span className="font-semibold" data-id="element-6625">
                                {downgradedMembers.length}
                              </span>{' '}
                              member{downgradedMembers.length !== 1 ? 's' : ''}{' '}
                              will be downgraded and may lose tier-specific
                              benefits
                            </p>
                          </div>}
                      </div>;
            })()}
              </div>

              {/* Modal Footer */}
              <div className="px-6 py-4 border-t border-[#E2E8F0] flex items-center justify-end gap-3" data-id="element-6626">
                <Button variant="ghost" size="md" onClick={() => setIsTierModalOpen(false)} data-id="element-6627">
                  Cancel
                </Button>
                <Button variant="primary" size="md" disabled={!selectedTargetTier || !tierReasonPreset} onClick={() => {
              setIsTierModalOpen(false);
            }} data-id="element-6628">
                  Apply Change
                </Button>
              </div>
            </div>
          </div>
        </>}

      {/* Change Loyalty Status Modal */}
      {isStatusModalOpen && <>
          <div className="fixed inset-0 bg-black/50 z-50" onClick={() => setIsStatusModalOpen(false)} data-id="element-6629" />
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-id="element-6630">
            <div className="bg-white rounded-lg shadow-xl w-[420px] max-h-[90vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()} data-id="element-6631">
              {/* Modal Header */}
              <div className="px-6 py-4 border-b border-[#E2E8F0] flex items-center justify-between" data-id="element-6632">
                <h2 className="text-lg font-semibold text-[#0F172A]" data-id="element-6633">
                  Change Loyalty Status
                </h2>
                <IconButton icon={<X className="w-5 h-5" data-id="element-6635" />} label="Close modal" variant="ghost" onClick={() => setIsStatusModalOpen(false)} data-id="element-6634" />
              </div>

              {/* Modal Content */}
              <div className="px-6 py-6 space-y-5 overflow-y-auto" data-id="element-6636">
                <p className="text-sm text-[#475569]" data-id="element-6637">
                  Update status for{' '}
                  <span className="font-semibold text-[#0F172A]" data-id="element-6638">
                    {selectedMembers.size}
                  </span>{' '}
                  selected members
                </p>

                {/* Status Action Toggle */}
                <div data-id="element-6639">
                  <label className="block text-xs font-medium text-[#475569] mb-2" data-id="element-6640">
                    Action
                  </label>
                  <div className="flex gap-4" data-id="element-6641">
                    <label className="flex items-center gap-2 cursor-pointer" data-id="element-6642">
                      <div className={`w-4 h-4 rounded-full border flex items-center justify-center ${statusAction === 'pause' ? 'border-[#0EA5E9]' : 'border-[#CBD5E1]'}`} onClick={() => setStatusAction('pause')} data-id="element-6643">
                        {statusAction === 'pause' && <div className="w-2 h-2 rounded-full bg-[#0EA5E9]" data-id="element-6644" />}
                      </div>
                      <span className="text-sm text-[#0F172A]" data-id="element-6645">
                        Pause Loyalty
                      </span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer" data-id="element-6646">
                      <div className={`w-4 h-4 rounded-full border flex items-center justify-center ${statusAction === 'resume' ? 'border-[#0EA5E9]' : 'border-[#CBD5E1]'}`} onClick={() => setStatusAction('resume')} data-id="element-6647">
                        {statusAction === 'resume' && <div className="w-2 h-2 rounded-full bg-[#0EA5E9]" data-id="element-6648" />}
                      </div>
                      <span className="text-sm text-[#0F172A]" data-id="element-6649">
                        Resume Loyalty
                      </span>
                    </label>
                  </div>
                </div>

                {/* Info Notice & Skip Notice */}
                {(() => {
              const targetMembers = mockMembers.filter(m => selectedMembers.has(m.id));
              const totalSelected = targetMembers.length;
              const alreadyPaused = targetMembers.filter(m => m.status === 'Paused').length;
              const alreadyActive = targetMembers.filter(m => m.status === 'Active').length;
              return <div className="space-y-3" data-id="element-6650">
                      {/* Info notice */}
                      <div className="bg-[#DBEAFE] border border-[#BFDBFE] rounded-[6px] px-4 py-3 flex items-start gap-2.5" data-id="element-6651">
                        <Info className="w-4 h-4 text-[#2563EB] mt-0.5 shrink-0" data-id="element-6652" />
                        <p className="text-[13px] text-[#1E40AF]" data-id="element-6653">
                          {statusAction === 'pause' ? 'Paused members will stop earning points on purchases but will retain their current balance and tier' : 'Members will begin earning points again on their next qualifying purchase'}
                        </p>
                      </div>

                      {/* Skip notice */}
                      {statusAction === 'pause' && alreadyPaused > 0 && <div className="bg-[#F1F5F9] rounded-[6px] px-4 py-3" data-id="element-6654">
                          <p className="text-[13px] text-[#475569]" data-id="element-6655">
                            <span className="font-semibold text-[#0F172A]" data-id="element-6656">
                              {alreadyPaused}
                            </span>{' '}
                            of{' '}
                            <span className="font-semibold text-[#0F172A]" data-id="element-6657">
                              {totalSelected}
                            </span>{' '}
                            selected members are already paused and will be
                            skipped
                          </p>
                        </div>}
                      {statusAction === 'resume' && alreadyActive > 0 && <div className="bg-[#F1F5F9] rounded-[6px] px-4 py-3" data-id="element-6658">
                          <p className="text-[13px] text-[#475569]" data-id="element-6659">
                            <span className="font-semibold text-[#0F172A]" data-id="element-6660">
                              {alreadyActive}
                            </span>{' '}
                            of{' '}
                            <span className="font-semibold text-[#0F172A]" data-id="element-6661">
                              {totalSelected}
                            </span>{' '}
                            selected members are already active and will be
                            skipped
                          </p>
                        </div>}
                    </div>;
            })()}

                {/* Reason (optional) */}
                <div data-id="element-6662">
                  <label className="block text-[12px] font-medium text-[#475569] mb-1.5" data-id="element-6663">
                    Reason <span className="text-[#94A3B8]" data-id="element-6664">(optional)</span>
                  </label>
                  <input type="text" value={statusReason} onChange={e => setStatusReason(e.target.value)} placeholder="e.g. Seasonal pause" className="w-full h-[40px] px-3 bg-white border border-[#CBD5E1] rounded-[6px] text-[14px] text-[#0F172A] placeholder:text-[#94A3B8] focus:outline-none focus:border-[#0EA5E9] focus:ring-[3px] focus:ring-[#0EA5E9]/20 transition-all duration-200" data-id="element-6665" />
                </div>

                {/* Notify Members Checkbox */}
                <div data-id="element-6666">
                  <label className="flex items-start gap-3 cursor-pointer" data-id="element-6667">
                    <div onClick={() => setNotifyMembers(!notifyMembers)} className={`w-[18px] h-[18px] mt-0.5 rounded border flex items-center justify-center shrink-0 transition-colors ${notifyMembers ? 'bg-[#0EA5E9] border-[#0EA5E9]' : 'border-[#CBD5E1] bg-white'}`} data-id="element-6668">
                      {notifyMembers && <svg width="12" height="12" viewBox="0 0 12 12" fill="none" data-id="element-6669">
                          <path d="M2.5 6L5 8.5L9.5 3.5" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-6670" />
                        </svg>}
                    </div>
                    <span className="text-[14px] font-medium text-[#0F172A]" data-id="element-6671">
                      Notify members by email
                    </span>
                  </label>
                </div>
              </div>

              {/* Modal Footer */}
              <div className="px-6 py-4 border-t border-[#E2E8F0] flex items-center justify-end gap-3" data-id="element-6672">
                <Button variant="ghost" size="md" onClick={() => setIsStatusModalOpen(false)} data-id="element-6673">
                  Cancel
                </Button>
                <Button variant="primary" size="md" onClick={() => {
              setIsStatusModalOpen(false);
            }} data-id="element-6674">
                  Apply Change
                </Button>
              </div>
            </div>
          </div>
        </>}

      {/* Toasts */}
      {toasts.map(toast => <Toast key={toast.id} {...toast} onClose={removeToast} data-id="element-6675" />)}
    </div>;
}